<template>
  <v-card-text>
    <v-select
      v-model="localForm.stillLookingForJob"
      :items="booleanNotSureOptions"
      item-title="name"
      return-object="true"
      variant="outlined"
      :error-messages="getErrorMessages('stillLookingForJob').value"
      :is-required="isRequired('stillLookingForJob')"
      @blur="v$.stillLookingForJob?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Are they still looking for a job?
        <span v-if="isRequired('stillLookingForJob')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-select
      v-if="localForm.stillLookingForJob"
      v-model="localForm.newJobLookingFor"
      :items="nextJobPriorities"
      item-title="name"
      return-object="true"
      variant="outlined"
      multiple
      :error-messages="getErrorMessages('newJobLookingFor').value"
      :is-required="isRequired('newJobLookingFor')"
      @blur="v$.newJobLookingFor?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        What are you looking for in your new job?
        <span v-if="isRequired('newJobLookingFor')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-text-field
      v-if="
        localForm.newJobLookingFor &&
        localForm.newJobLookingFor.some((item) => item.value === 'OTHER')
      "
      v-model="localForm.otherJobPriority"
      label="Please specify"
      variant="outlined"
      :error-messages="getErrorMessages('otherJobPriority').value"
      @blur="v$.otherJobPriority?.$touch?.()"
      @input="updateForm"
    />
  </v-card-text>
</template>

<script setup>
import { watch, reactive, onMounted, computed } from "vue";
import useVuelidate from "@vuelidate/core";
import { defineProps, defineEmits } from "vue";

const props = defineProps({
  form: Object, // parent passes full form object
  rules: Object, // parent passes validation rules
});

const emit = defineEmits(["update:form"]);

// Emit changes back to parent
const updateForm = () => {
  emit("update:form", { ...localForm });
};

const localForm = reactive({ ...props.form });

const requiredFields = computed(() => {
  const base = ["stillLookingForJob"];
  if (localForm.stillLookingForJob?.value === "YES") {
    base.push("newJobLookingFor");
  }
  return base;
});

const completionPercent = computed(() => {
  const total = requiredFields.value.length;

  const filled = requiredFields.value.filter((field) => {
    return (
      localForm[field] !== null &&
      localForm[field] !== undefined &&
      localForm[field] !== ""
    );
  }).length;

  return Math.round((filled / total) * 100);
});

const isRequired = (field) => requiredFields.value.includes(field);

const v$ = useVuelidate(props.rules, localForm);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : [],
  );

const booleanNotSureOptions = [
  { name: "Yes", value: "YES" },
  { name: "No", value: "NO" },
  { name: "Not sure", value: "NOT_SURE" },
];

const nextJobPriorities = [
  { name: "Salary", value: "SALARY" },
  { name: "Location", value: "LOCATION" },
  { name: "Stability", value: "STABILITY" },
  { name: "More suitable role", value: "MORE_SUITABLE_ROLE" },
  { name: "Working hours", value: "WORKING_HOURS" },
  { name: "Employment type", value: "EMPLOYMENT_TYPE" },
  { name: "Other", value: "OTHER" },
];

// Watch parent updates in case they change
watch(
  () => props.form,
  (newVal) => {
    Object.assign(localForm, newVal);
  },
  { deep: true, immediate: true },
);

watch(
  completionPercent,
  (val) => {
    emit("update:completion", val);
  },
  { immediate: true },
);

onMounted(async () => {});
</script>
